/**
 * Termék Építő v2
 * – LISTA opciók auto-sorszám (kod=1,2,3…) → képletben azonnal elérhető
 * – Ár per opció → VALUE_PRICE szabály
 * – Egyéni változók összecsukható "Haladó" szekcióban
 * – Végső ár képlete → BASE_PRICE_FORMULA szabály
 * – Helyes szabály-sorrend: VARIABLE_MAP → VALUE_PRICE → SIZE_FORMULA → BASE_PRICE_FORMULA → CONDITIONAL_ERROR
 */
import { useState, useEffect, useRef } from 'react';
import { get, post, patch, del } from '../../lib/api';
import Button from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { useFeatureContext } from '../../contexts/FeatureContext';

// ── Típusok ───────────────────────────────────────────────────────────────────

let _id = 0;
function uid() { return String(++_id); }

const NAV_UNIT_MAP: Record<string, string> = {
  db: 'PIECE', m: 'METER', m2: 'SQUARE_METER', m3: 'CUBIC_METER',
  kg: 'KILOGRAM', h: 'HOUR', nap: 'DAY', l: 'LITER',
  cs: 'PIECE', készlet: 'PIECE',
};
function unitToNavCode(u: string): string {
  return NAV_UNIT_MAP[u?.toLowerCase()] || 'PIECE';
}

interface HVar { id: string; key: string; value: string; }

interface Opt {
  id: string;
  label: string;
  price: string;       // Ft, üresen hagyható → VALUE_PRICE
  extraVars: HVar[];   // egyéni változók a auto-index mellé
}

interface Spec {
  id: string; kod: string; nev: string;
  tipus: 'LISTA' | 'SZAM' | 'SZOVEG';
  opts: Opt[];
  egyseg: string; minE: string; maxE: string;
  sortOrder: number;
}

interface Formula {
  id: string; resultVar: string; expression: string; minValue: string;
}

interface Cond { id: string; left: string; op: string; right: string; }

interface Validation {
  id: string; conditions: Cond[];
  logics: ('AND' | 'OR')[];  // length = conditions.length - 1, connector between adjacent conditions
  message: string;
}

interface Related {
  id: string; mode: 'existing' | 'new';
  existingId: string; existingName: string; existingSku: string;
  newName: string; newKind: 'product' | 'service'; newPrice: number;
  newSku: string; skuLocked: boolean; newUnit: string;
  relType: 'REQUIRED' | 'SUGGESTED';
  qtyMode: 'fixed' | 'var'; qtyFixed: string; qtyVar: string;
}

interface AutoItemRule {
  id: string;
  inputKod: string;    // melyik konfig mezőt figyeli
  inputValue: string;  // milyen értékre
  itemSku: string;     // hozzáadandó termék SKU
  itemName: string;    // megjelenítési név
  qtyMode: 'fixed' | 'var'; // fix szám vagy képlet-változó
  qty: string;         // fix mennyiség (szám, stringként)
  qtyVar: string;      // képlet-változó neve (pl. "fm")
}

// ── Segédfüggvények ───────────────────────────────────────────────────────────

function skuFrom(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase().replace(/[^A-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 14) || 'CIKK';
}
function kodFrom(s: string) {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 20) || 'param';
}
function fmtFt(n: number) { return n.toLocaleString('hu-HU') + ' Ft'; }

const CIRCLES = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
function circle(i: number) { return CIRCLES[i] ?? String(i + 1); }

// ── Stílusok ─────────────────────────────────────────────────────────────────

const INP: React.CSSProperties = {
  fontSize: 13, padding: '7px 10px', border: '1.5px solid var(--line)',
  borderRadius: 7, background: '#fff', width: '100%', outline: 'none', boxSizing: 'border-box',
};
const LBL: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase',
  letterSpacing: '.05em', display: 'block', marginBottom: 3,
};
const PANEL: React.CSSProperties = {
  background: '#fffbf5', border: '1.5px solid var(--orange)', borderRadius: 10, padding: 14, marginBottom: 10,
};
const ROW: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8,
  padding: '9px 12px', border: '1px solid var(--line)', borderRadius: 8, background: '#fff', marginBottom: 4,
};
const DEL: React.CSSProperties = {
  border: 'none', background: 'none', cursor: 'pointer', fontSize: 16,
  lineHeight: 1, padding: '0 2px', color: '#dc2626', flexShrink: 0,
};

function togBtn(on: boolean, ac = 'var(--orange)', ab = '#fff7ed'): React.CSSProperties {
  return {
    padding: '6px 10px', borderRadius: 7, border: `1.5px solid ${on ? ac : 'var(--line)'}`,
    background: on ? ab : '#fff', cursor: 'pointer', fontSize: 12,
    fontWeight: on ? 700 : 500, color: on ? ac : 'var(--slate)',
  };
}
function chip(bg: string, fg: string, bd: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
    borderRadius: 20, background: bg, color: fg, fontSize: 11,
    fontWeight: 600, border: `1px solid ${bd}`, cursor: 'pointer',
  };
}
function codeBadge(txt: string) {
  return (
    <code style={{ fontSize: 11, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 4, padding: '1px 6px' }}>
      {txt}
    </code>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODUL-SZINTŰ KOMPONENSEK (nem render-belsőben definiálva!)
// ─────────────────────────────────────────────────────────────────────────────

// ── Section ───────────────────────────────────────────────────────────────────

function Section({ title, subtitle, onAdd, addLabel, locked, children }: {
  title: string; subtitle: string; onAdd?: () => void; addLabel?: string;
  locked?: boolean; children: React.ReactNode;
}) {
  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>{title}</div>
          <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 2 }}>{subtitle}</div>
        </div>
        {onAdd && addLabel && !locked && <Button size="sm" onClick={onAdd}>{addLabel}</Button>}
      </div>
      {locked && (
        <div style={{ fontSize: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '9px 12px', marginBottom: 8 }}>
          Aktiváld a <strong>Konfigurátor Alap</strong> modult a Beállítások → Számlázás oldalon.
        </div>
      )}
      {!locked && children}
    </div>
  );
}

// ── SpecFormPanel ─────────────────────────────────────────────────────────────

function SpecFormPanel({ sortOrder, onSave, onCancel }: {
  sortOrder: number;
  onSave: (s: Spec) => void;
  onCancel: () => void;
}) {
  const [nev, setNev] = useState('');
  const [kod, setKod] = useState('');
  const [kodLocked, setKodLocked] = useState(false);
  const [tipus, setTipus] = useState<'LISTA' | 'SZAM' | 'SZOVEG'>('LISTA');
  const [egyseg, setEgyseg] = useState('');
  const [minE, setMinE] = useState('');
  const [maxE, setMaxE] = useState('');
  const [opts, setOpts] = useState<Opt[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Inline "add option" form state
  const [showAdd, setShowAdd] = useState(false);
  const [aLabel, setALabel] = useState('');
  const [aPrice, setAPrice] = useState('');
  const [aVars, setAVars] = useState<HVar[]>([]);
  const [aHvKey, setAHvKey] = useState('');
  const [aHvVal, setAHvVal] = useState('');
  const [aAdv, setAAdv] = useState(false);

  useEffect(() => { if (!kodLocked) setKod(kodFrom(nev)); }, [nev]);

  function toggleExpanded(id: string) {
    setExpandedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function addHv() {
    if (!aHvKey.trim()) return;
    setAVars(p => [...p, { id: uid(), key: aHvKey.trim(), value: aHvVal }]);
    setAHvKey(''); setAHvVal('');
  }

  function commitOpt() {
    if (!aLabel.trim()) return;
    const extraVars = [...aVars, ...(aHvKey.trim() ? [{ id: uid(), key: aHvKey.trim(), value: aHvVal }] : [])];
    setOpts(p => [...p, { id: uid(), label: aLabel.trim(), price: aPrice, extraVars }]);
    setALabel(''); setAPrice(''); setAVars([]); setAHvKey(''); setAHvVal(''); setAAdv(false); setShowAdd(false);
  }

  function resetAddForm() {
    setShowAdd(false); setALabel(''); setAPrice(''); setAVars([]); setAHvKey(''); setAHvVal(''); setAAdv(false);
  }

  function delOptVar(optId: string, varId: string) {
    setOpts(p => p.map(o => o.id !== optId ? o : { ...o, extraVars: o.extraVars.filter(v => v.id !== varId) }));
  }

  const canSave = nev.trim() && kod.trim() && (tipus !== 'LISTA' || opts.length > 0);

  return (
    <div style={PANEL}>
      {/* Alap */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={LBL}>Megnevezés *</label>
          <input style={INP} value={nev} onChange={e => setNev(e.target.value)} placeholder="pl. Szín, Méret, Szegő szín" autoFocus />
        </div>
        <div>
          <label style={LBL}>Kód * (képletben)</label>
          <input style={{ ...INP, fontFamily: 'monospace', width: 120 }} value={kod}
            onChange={e => { setKodLocked(true); setKod(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')); }} />
        </div>
      </div>

      {/* Típus */}
      <div style={{ marginBottom: 10 }}>
        <label style={LBL}>Típus</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['LISTA', 'SZAM', 'SZOVEG'] as const).map(t => (
            <button key={t} style={togBtn(tipus === t)} onClick={() => setTipus(t)}>
              {t === 'LISTA' ? 'Lista (választó)' : t === 'SZAM' ? 'Szám (méret/érték)' : 'Szöveg (szabad)'}
            </button>
          ))}
        </div>
      </div>

      {/* SZAM mezők */}
      {tipus === 'SZAM' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
          <div><label style={LBL}>Egység</label><input style={INP} value={egyseg} onChange={e => setEgyseg(e.target.value)} placeholder="cm, fm, db" /></div>
          <div><label style={LBL}>Min</label><input type="number" inputMode="decimal" style={INP} value={minE} onChange={e => setMinE(e.target.value)} /></div>
          <div><label style={LBL}>Max</label><input type="number" inputMode="decimal" style={INP} value={maxE} onChange={e => setMaxE(e.target.value)} /></div>
        </div>
      )}

      {/* LISTA opciók */}
      {tipus === 'LISTA' && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <label style={{ ...LBL, marginBottom: 0 }}>Opciók</label>
            <span style={{ fontSize: 10, color: 'var(--slate)', opacity: .85 }}>
              Minden opcióhoz automatikusan: <code style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 3, padding: '0 4px' }}>{kod || 'kod'} = 1, 2, 3…</code> — képletekben azonnal elérhető
            </span>
          </div>

          {/* Meglevő opciók */}
          {opts.map((o, oi) => (
            <div key={o.id} style={{ border: '1px solid var(--line)', borderRadius: 8, marginBottom: 4, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#fff' }}>
                <span style={{ fontSize: 14, color: '#1d4ed8', fontWeight: 700, flexShrink: 0, minWidth: 22, textAlign: 'center' }}>{circle(oi)}</span>
                <span style={{ fontSize: 13, flex: 1 }}>{o.label}</span>
                {o.price && parseFloat(o.price) > 0 && (
                  <span style={{ fontSize: 11, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '1px 8px', fontWeight: 700 }}>
                    {fmtFt(parseFloat(o.price))}
                  </span>
                )}
                {o.extraVars.length > 0 && (
                  <span style={{ fontSize: 10, color: '#7e22ce', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '1px 7px' }}>
                    +{o.extraVars.length} var
                  </span>
                )}
                <button style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--slate)', padding: '0 4px' }} onClick={() => toggleExpanded(o.id)}>
                  {expandedIds.has(o.id) ? '▲' : '▶'}
                </button>
                <button style={DEL} onClick={() => setOpts(p => p.filter((_, i) => i !== oi))}>×</button>
              </div>
              {expandedIds.has(o.id) && (
                <div style={{ background: '#f8faff', borderTop: '1px solid var(--line)', padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 5 }}>
                    <strong>Auto:</strong> {codeBadge(`${kod || 'kod'} = ${oi + 1}`)}
                    {o.price && parseFloat(o.price) > 0 && <> &nbsp;<strong>Ár:</strong> {codeBadge(`egysegar = ${fmtFt(parseFloat(o.price))}`)}</>}
                  </div>
                  {o.extraVars.length > 0 && (
                    <div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#7e22ce', textTransform: 'uppercase', marginRight: 6 }}>Egyéni változók:</span>
                      {o.extraVars.map(hv => (
                        <span key={hv.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginRight: 6, marginBottom: 2 }}>
                          {codeBadge(`${hv.key} = ${hv.value}`)}
                          <button style={{ ...DEL, fontSize: 12 }} onClick={() => delOptVar(o.id, hv.id)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Opció hozzáadása form */}
          {showAdd ? (
            <div style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, marginTop: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px auto', gap: 6, alignItems: 'end', marginBottom: 8 }}>
                <div>
                  <label style={LBL}>Opció neve *</label>
                  <input style={INP} value={aLabel} onChange={e => setALabel(e.target.value)}
                    placeholder={`pl. ${opts.length === 0 ? 'Fehér, Kék, 35×100 cm' : opts[opts.length - 1]?.label || 'következő opció'}`}
                    autoFocus onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitOpt(); }}} />
                </div>
                <div>
                  <label style={LBL}>Ár (Ft) — opcionális</label>
                  <input type="number" inputMode="decimal" style={INP} value={aPrice} onChange={e => setAPrice(e.target.value)} placeholder="0" />
                </div>
                <button style={{ ...togBtn(aAdv, '#7e22ce', '#faf5ff'), alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
                  onClick={() => setAAdv(v => !v)}>
                  {aAdv ? '▲' : '▶'} Haladó változók
                </button>
              </div>

              {/* Haladó: egyéni változók (összecsukható) */}
              {aAdv && (
                <div style={{ background: '#fff', border: '1px solid #e9d5ff', borderRadius: 7, padding: 10, marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#7e22ce', fontWeight: 700, marginBottom: 4 }}>Egyéni változók</div>
                  <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 6 }}>
                    Ez az opció automatikusan kapja: {codeBadge(`${kod || 'kod'} = ${opts.length + 1}`)}<br />
                    Ha más numerikus változókat is be kell állítani (pl. szel=35, mag=100 méretekhez), add meg alább:
                  </div>
                  {aVars.map((hv, i) => (
                    <span key={hv.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginRight: 6, marginBottom: 4 }}>
                      {codeBadge(`${hv.key} = ${hv.value}`)}
                      <button style={{ ...DEL, fontSize: 12 }} onClick={() => setAVars(p => p.filter((_, j) => j !== i))}>×</button>
                    </span>
                  ))}
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 4 }}>
                    <input style={{ ...INP, width: 90, fontFamily: 'monospace' }} value={aHvKey}
                      onChange={e => setAHvKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} placeholder="kulcs" />
                    <span style={{ fontSize: 12, color: 'var(--slate)' }}>=</span>
                    <input type="number" inputMode="decimal" style={{ ...INP, width: 80 }} value={aHvVal} onChange={e => setAHvVal(e.target.value)} placeholder="szám" />
                    <button onClick={addHv} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--line)', background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>+ Változó</button>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button onClick={resetAddForm} style={{ fontSize: 12, padding: '5px 10px', border: '1px solid var(--line)', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Mégse</button>
                <button onClick={commitOpt} disabled={!aLabel.trim()}
                  style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: 'none', background: aLabel.trim() ? '#1d4ed8' : '#e5e7eb', color: aLabel.trim() ? '#fff' : 'var(--slate)', cursor: aLabel.trim() ? 'pointer' : 'default', fontWeight: 700 }}>
                  Opció hozzáadása
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowAdd(true)}
              style={{ width: '100%', padding: '8px', border: '1.5px dashed var(--line)', borderRadius: 7, background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--slate)', fontWeight: 600, textAlign: 'center', marginTop: 4 }}>
              + Opció hozzáadása
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Mégse</Button>
        <Button size="sm" onClick={() => onSave({ id: uid(), kod: kod.trim(), nev: nev.trim(), tipus, opts, egyseg, minE, maxE, sortOrder })}
          disabled={!canSave}>
          Spec hozzáadása
        </Button>
      </div>
    </div>
  );
}

// ── SpecCard ──────────────────────────────────────────────────────────────────

function SpecCard({ s, onDelete }: { s: Spec; onDelete: () => void }) {
  const TC: Record<string, React.CSSProperties> = {
    LISTA: { background: '#eff6ff', color: '#1d4ed8' },
    SZAM: { background: '#f0fdf4', color: '#15803d' },
    SZOVEG: { background: '#faf5ff', color: '#7e22ce' },
  };
  return (
    <div style={ROW}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>{s.kod}</code>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, ...TC[s.tipus] }}>{s.tipus}</span>
          <span style={{ fontSize: 13, color: 'var(--slate)' }}>{s.nev}</span>
        </div>
        {s.tipus === 'LISTA' && s.opts.length > 0 && (
          <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {s.opts.map((o, oi) => (
              <span key={o.id} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#f3f4f6', color: 'var(--slate)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#1d4ed8', fontWeight: 700 }}>{circle(oi)}</span>
                {o.label}
                {o.price && parseFloat(o.price) > 0 && <span style={{ color: '#15803d', fontWeight: 600 }}>·{fmtFt(parseFloat(o.price))}</span>}
                {o.extraVars.length > 0 && <span style={{ color: '#7e22ce', opacity: .8 }}>+{o.extraVars.length}</span>}
              </span>
            ))}
          </div>
        )}
        {s.tipus === 'SZAM' && (
          <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 2 }}>
            {s.egyseg && `Egység: ${s.egyseg}`}{s.minE && ` · min: ${s.minE}`}{s.maxE && ` · max: ${s.maxE}`}
          </div>
        )}
      </div>
      <button style={DEL} onClick={onDelete}>×</button>
    </div>
  );
}

// ── FormulaFormPanel ──────────────────────────────────────────────────────────

function FormulaFormPanel({ availVars, onSave, onCancel }: {
  availVars: string[];
  onSave: (f: Formula) => void;
  onCancel: () => void;
}) {
  const [resultVar, setResultVar] = useState('');
  const [expr, setExpr] = useState('');
  const [minV, setMinV] = useState('');
  const exprRef = useRef<HTMLInputElement>(null);

  function insert(text: string) {
    const el = exprRef.current;
    if (!el) { setExpr(e => e + text); return; }
    const s = el.selectionStart ?? el.value.length;
    const e2 = el.selectionEnd ?? el.value.length;
    const nv = el.value.slice(0, s) + text + el.value.slice(e2);
    setExpr(nv);
    requestAnimationFrame(() => {
      if (!exprRef.current) return;
      exprRef.current.setSelectionRange(s + text.length, s + text.length);
      exprRef.current.focus();
    });
  }

  return (
    <div style={PANEL}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, alignItems: 'end', marginBottom: 10 }}>
        <div>
          <label style={LBL}>Változó neve *</label>
          <input style={{ ...INP, fontFamily: 'monospace', width: 90 }} value={resultVar} autoFocus
            onChange={e => setResultVar(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            placeholder="fm" />
        </div>
        <div>
          <label style={LBL}>= Képlet * <span style={{ fontWeight: 400, textTransform: 'none', opacity: .7 }}>— kattints a változókra / operátorokra</span></label>
          <input ref={exprRef} style={{ ...INP, fontFamily: 'monospace' }} value={expr}
            onChange={e => setExpr(e.target.value)} placeholder="pl. (2*szel + 2*mag) / 100" />
        </div>
      </div>

      {/* Token palette */}
      {availVars.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', marginBottom: 4 }}>Elérhető változók (kattints → beillesztés)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {availVars.map(v => (
              <button key={v} onClick={() => insert(v)} style={chip('#eff6ff', '#1d4ed8', '#bfdbfe')}>{v}</button>
            ))}
            {['+', '-', '*', '/', '(', ')'].map(op => (
              <button key={op} onClick={() => insert(op)} style={{ ...chip('#f3f4f6', 'var(--navy)', '#e5e7eb'), fontFamily: 'monospace', fontWeight: 700, fontSize: 14 }}>{op}</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginBottom: 10, maxWidth: 160 }}>
        <label style={LBL}>Minimum érték <span style={{ fontWeight: 400, textTransform: 'none', opacity: .7 }}>(opcionális)</span></label>
        <input type="number" inputMode="decimal" style={INP} value={minV} onChange={e => setMinV(e.target.value)} placeholder="pl. 1.0" />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Mégse</Button>
        <Button size="sm" onClick={() => onSave({ id: uid(), resultVar: resultVar.trim(), expression: expr.trim(), minValue: minV })}
          disabled={!resultVar.trim() || !expr.trim()}>Képlet hozzáadása</Button>
      </div>
    </div>
  );
}

// ── FormulaCard ───────────────────────────────────────────────────────────────

function FormulaCard({ f, onDelete }: { f: Formula; onDelete: () => void }) {
  return (
    <div style={ROW}>
      <div style={{ flex: 1 }}>
        <code style={{ fontSize: 13, color: 'var(--navy)' }}><strong>{f.resultVar}</strong> = {f.expression}</code>
        {f.minValue && <span style={{ fontSize: 11, color: 'var(--slate)', marginLeft: 8 }}>min: {f.minValue}</span>}
      </div>
      <button style={DEL} onClick={onDelete}>×</button>
    </div>
  );
}

// ── ValidationFormPanel ───────────────────────────────────────────────────────

function ValidationFormPanel({ specs, formulaVars, onSave, onCancel }: {
  specs: Spec[]; formulaVars: string[];
  onSave: (v: Validation) => void; onCancel: () => void;
}) {
  const firstLeft = specs[0]?.kod || '';
  const [conds, setConds] = useState<Cond[]>([{ id: uid(), left: firstLeft, op: '=', right: '' }]);
  // Per-connector logics: logics[i] = összekötő conds[i] és conds[i+1] között
  const [logics, setLogics] = useState<('AND' | 'OR')[]>([]);
  const [msg, setMsg] = useState('');

  const leftOptions = [
    ...specs.map(s => ({ value: s.kod, label: `${s.nev} (${s.kod})`, specTipus: s.tipus })),
    ...specs.flatMap(s => s.opts.flatMap(o => o.extraVars.map(hv => ({ value: hv.key, label: `${hv.key} (egyéni var)`, specTipus: 'SZAM' as const })))).filter((v, i, a) => a.findIndex(x => x.value === v.value) === i),
    ...formulaVars.map(v => ({ value: v, label: `${v} (képlet)`, specTipus: 'SZAM' as const })),
  ];

  const OPS = ['=', '!=', '<', '>', '<=', '>='];
  const OP_L: Record<string, string> = { '=': '= egyenlő', '!=': '≠ nem egyenlő', '<': '< kisebb', '>': '> nagyobb', '<=': '≤ kisebb/egyenlő', '>=': '≥ nagyobb/egyenlő' };

  function updCond(id: string, p: Partial<Cond>) {
    setConds(prev => prev.map(c => c.id === id ? { ...c, ...p } : c));
  }

  function addCond() {
    setConds(p => [...p, { id: uid(), left: firstLeft, op: '=', right: '' }]);
    setLogics(p => [...p, 'AND']);
  }

  function removeCond(id: string, ci: number) {
    setConds(p => p.filter(x => x.id !== id));
    // Ha az elsőt töröljük: az utána lévő connector törlendő (logics[0])
    // Ha a többit: az előtte lévő connector törlendő (logics[ci-1])
    setLogics(p => {
      const n = [...p];
      n.splice(ci === 0 ? 0 : ci - 1, 1);
      return n;
    });
  }

  function toggleLogic(idx: number) {
    setLogics(p => p.map((l, i) => i === idx ? (l === 'AND' ? 'OR' : 'AND') : l));
  }

  return (
    <div style={PANEL}>
      <label style={{ ...LBL, marginBottom: 8 }}>HA ezek a feltételek teljesülnek:</label>
      <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 8, opacity: .8 }}>
        Tipp: azonos üzenetű szabályok ismétlés nélkül jelennek meg. Ha "A ÉS (B VAGY C)" logikát akarsz, hozz létre két külön feltételt: "A ÉS B" és "A ÉS C" — mindkettőhöz ugyanazt az üzenetet írj.
      </div>

      {conds.map((c, ci) => {
        const listaSpec = specs.find(s => s.kod === c.left && s.tipus === 'LISTA');
        return (
          <div key={c.id}>
            {ci > 0 && (
              <div style={{ display: 'flex', gap: 6, margin: '6px 0', alignItems: 'center' }}>
                <button
                  style={togBtn(logics[ci - 1] === 'AND', '#1d4ed8', '#eff6ff')}
                  onClick={() => toggleLogic(ci - 1)}>
                  ÉS
                </button>
                <button
                  style={togBtn(logics[ci - 1] === 'OR', '#7e22ce', '#faf5ff')}
                  onClick={() => toggleLogic(ci - 1)}>
                  VAGY
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <select value={c.left} onChange={e => updCond(c.id, { left: e.target.value, right: '' })}
                style={{ ...INP, width: 'auto', flex: 2 }}>
                {leftOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={c.op} onChange={e => updCond(c.id, { op: e.target.value })}
                style={{ ...INP, width: 'auto', flex: 1 }}>
                {OPS.map(op => <option key={op} value={op}>{OP_L[op]}</option>)}
              </select>
              {listaSpec ? (
                <select value={c.right} onChange={e => updCond(c.id, { right: e.target.value })}
                  style={{ ...INP, width: 'auto', flex: 2 }}>
                  <option value="">– válassz –</option>
                  {listaSpec.opts.map(o => <option key={o.id} value={o.label}>{o.label}</option>)}
                </select>
              ) : (
                <input style={{ ...INP, flex: 2 }} value={c.right}
                  onChange={e => updCond(c.id, { right: e.target.value })} placeholder="érték" />
              )}
              {conds.length > 1 && (
                <button style={DEL} onClick={() => removeCond(c.id, ci)}>×</button>
              )}
            </div>
          </div>
        );
      })}

      <button onClick={addCond}
        style={{ fontSize: 11, padding: '4px 10px', border: '1px dashed var(--line)', borderRadius: 6, background: 'none', cursor: 'pointer', color: 'var(--slate)', marginBottom: 12 }}>
        + Feltétel hozzáadása
      </button>

      <div style={{ marginBottom: 10 }}>
        <label style={LBL}>AKKOR hibaüzenet *</label>
        <input style={INP} value={msg} onChange={e => setMsg(e.target.value)}
          placeholder="pl. A tengerészkék maximum 69×100 cm-es méretben készíthető" />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Mégse</Button>
        <Button size="sm"
          onClick={() => onSave({ id: uid(), conditions: conds, logics, message: msg })}
          disabled={!msg.trim() || conds.some(c => !c.left || !c.right)}>
          Feltétel hozzáadása
        </Button>
      </div>
    </div>
  );
}

// ── ValidationCard ────────────────────────────────────────────────────────────

function ValidationCard({ v, onDelete }: { v: Validation; onDelete: () => void }) {
  const SYM: Record<string, string> = { '=': '=', '!=': '≠', '<': '<', '>': '>', '<=': '≤', '>=': '≥' };
  return (
    <div style={ROW}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, marginBottom: 2 }}>
          HA{' '}
          {v.conditions.map((c, i) => (
            <span key={c.id}>
              {i > 0 && (
                <strong style={{ color: v.logics[i - 1] === 'OR' ? '#7e22ce' : '#1d4ed8', margin: '0 4px' }}>
                  {v.logics[i - 1] === 'OR' ? 'VAGY' : 'ÉS'}
                </strong>
              )}
              <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>{c.left} {SYM[c.op]} {c.right}</code>
            </span>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#dc2626' }}>→ Hiba: {v.message}</div>
      </div>
      <button style={DEL} onClick={onDelete}>×</button>
    </div>
  );
}

// ── AutoItemRuleEditor ────────────────────────────────────────────────────────

function AutoItemRuleEditor({ specs, formulaVars, onSave, onCancel }: {
  specs: Spec[];
  formulaVars: string[];
  onSave: (r: AutoItemRule) => void;
  onCancel: () => void;
}) {
  const [inputKod, setInputKod] = useState(specs[0]?.kod || '');
  const [inputValue, setInputValue] = useState('');
  const [skuMode, setSkuMode] = useState<'search' | 'template'>('search');
  const [skuTemplate, setSkuTemplate] = useState('');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [dropOpen, setDropOpen] = useState(false);
  const [selSku, setSelSku] = useState('');
  const [selName, setSelName] = useState('');
  const [qtyMode, setQtyMode] = useState<'fixed' | 'var'>('fixed');
  const [qty, setQty] = useState('1');
  const [qtyVar, setQtyVar] = useState(formulaVars[0] || '');

  const selectedSpec = specs.find(s => s.kod === inputKod);

  useEffect(() => {
    if (!search || selSku) return;
    const t = setTimeout(async () => {
      try {
        const r = await get<any>(`/items?search=${encodeURIComponent(search)}&pageSize=8`);
        setResults(Array.isArray(r) ? r : r?.data || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, selSku]);

  const ok = inputKod && inputValue.trim() && (skuMode === 'template' ? skuTemplate.trim() : selSku);

  return (
    <div style={PANEL}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={LBL}>Ha mező</label>
          <select style={INP} value={inputKod} onChange={e => { setInputKod(e.target.value); setInputValue(''); }}>
            {specs.map(s => <option key={s.kod} value={s.kod}>{s.nev} ({s.kod})</option>)}
          </select>
        </div>
        <div>
          <label style={LBL}>= Érték</label>
          {selectedSpec?.tipus === 'LISTA' ? (
            <select style={INP} value={inputValue} onChange={e => setInputValue(e.target.value)}>
              <option value="">— válassz —</option>
              {selectedSpec.opts.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
            </select>
          ) : (
            <input style={INP} value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder="pl. motoros" />
          )}
        </div>
      </div>
      <div style={{ marginBottom: 10, position: 'relative' }}>
        <label style={LBL}>Hozzáadandó termék *</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <button style={togBtn(skuMode === 'search')} onClick={() => setSkuMode('search')}>Konkrét termék</button>
          <button style={togBtn(skuMode === 'template')} onClick={() => setSkuMode('template')}>SKU sablon</button>
        </div>
        {skuMode === 'search' ? (
          selSku ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1.5px solid var(--orange)', borderRadius: 7, background: '#fff7ed' }}>
              <code style={{ fontWeight: 700, fontSize: 12 }}>{selSku}</code>
              <span style={{ fontSize: 13 }}>{selName}</span>
              <button style={{ ...DEL, marginLeft: 'auto' }} onClick={() => { setSelSku(''); setSelName(''); setSearch(''); }}>×</button>
            </div>
          ) : (
            <>
              <input style={INP} value={search} onChange={e => setSearch(e.target.value)}
                onFocus={() => setDropOpen(true)} onBlur={() => setTimeout(() => setDropOpen(false), 200)}
                placeholder="SKU vagy megnevezés..." />
              {dropOpen && results.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, border: '1px solid var(--line)', borderRadius: 8, background: '#fff', zIndex: 70, maxHeight: 150, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,.12)', marginTop: 2 }}>
                  {results.map(r => (
                    <button key={r.id} type="button" onMouseDown={() => { setSelSku(r.sku); setSelName(r.name); setSearch(r.name); }}
                      style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid #f3f4f6', background: 'none', cursor: 'pointer', fontSize: 12 }}>
                      <code style={{ fontWeight: 700 }}>{r.sku}</code>
                      <span style={{ color: 'var(--slate)' }}>{r.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )
        ) : (
          <div>
            <input style={{ ...INP, fontFamily: 'monospace' }} value={skuTemplate}
              onChange={e => setSkuTemplate(e.target.value)}
              placeholder="pl. SZEGO/{szego_szine}" />
            <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
              <span>Beilleszthető mezők:</span>
              {specs.map(s => (
                <button key={s.kod} type="button"
                  onClick={() => setSkuTemplate(t => t + '{' + s.kod + '}')}
                  style={chip('#f0f9ff', '#0369a1', '#bae6fd')}>
                  {'{' + s.kod + '}'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={LBL}>Mennyiség</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <button style={togBtn(qtyMode === 'fixed')} onClick={() => setQtyMode('fixed')}>Fix szám</button>
          <button style={togBtn(qtyMode === 'var')} onClick={() => setQtyMode('var')}>Képlet változó</button>
        </div>
        {qtyMode === 'fixed' ? (
          <input type="number" inputMode="decimal" style={{ ...INP, maxWidth: 120 }} value={qty} onChange={e => setQty(e.target.value)} min={0.001} />
        ) : (
          <div>
            {formulaVars.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {formulaVars.map(v => (
                  <button key={v} type="button" onClick={() => setQtyVar(v)}
                    style={chip(qtyVar === v ? '#f0fdf4' : '#f8fafc', qtyVar === v ? '#15803d' : 'var(--slate)', qtyVar === v ? '#bbf7d0' : 'var(--line)')}>
                    {v}
                  </button>
                ))}
              </div>
            ) : (
              <input style={{ ...INP, fontFamily: 'monospace', maxWidth: 120 }} value={qtyVar} onChange={e => setQtyVar(e.target.value)} placeholder="pl. fm, m2" />
            )}
            <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 4 }}>A kiválasztott változó értéke lesz a mennyiség (pl. fm = perimeter méterben)</div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button style={{ ...togBtn(false), color: 'var(--slate)' }} onClick={onCancel}>Mégse</button>
        <button style={togBtn(!!ok, '#15803d', '#f0fdf4')} disabled={!ok}
          onClick={() => ok && onSave({ id: uid(), inputKod, inputValue: inputValue.trim(), itemSku: skuMode === 'template' ? skuTemplate.trim() : selSku, itemName: skuMode === 'template' ? skuTemplate.trim() : selName, qtyMode, qty, qtyVar })}>
          Hozzáadás
        </button>
      </div>
    </div>
  );
}

// ── AddRelatedPanel ───────────────────────────────────────────────────────────

function AddRelatedPanel({ formulaVars, onSave, onCancel }: {
  formulaVars: string[];
  onSave: (r: Related) => void; onCancel: () => void;
}) {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [dropOpen, setDropOpen] = useState(false);
  const [selId, setSelId] = useState('');
  const [selName, setSelName] = useState('');
  const [selSku, setSelSku] = useState('');
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<'product' | 'service'>('service');
  const [newUnit, setNewUnit] = useState('h');
  const [newUnitLocked, setNewUnitLocked] = useState(false);
  const [newPrice, setNewPrice] = useState(0);
  const [newSku, setNewSku] = useState('');
  const [skuLocked, setSkuLocked] = useState(false);
  const [relType, setRelType] = useState<'REQUIRED' | 'SUGGESTED'>('REQUIRED');
  const [qtyMode, setQtyMode] = useState<'fixed' | 'var'>('fixed');
  const [qtyFixed, setQtyFixed] = useState('1');
  const [qtyVar, setQtyVar] = useState('');

  useEffect(() => { if (!skuLocked) setNewSku(skuFrom(newName)); }, [newName]);
  useEffect(() => { if (!newUnitLocked) setNewUnit(newKind === 'service' ? 'h' : 'db'); }, [newKind]);
  useEffect(() => {
    if (mode !== 'existing' || !search.trim() || selId) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await get<any>(`/items?search=${encodeURIComponent(search)}&pageSize=8`);
        setResults(Array.isArray(r) ? r : r?.data || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, mode, selId]);

  const ok = mode === 'existing' ? !!selId : !!newName.trim();

  function handleSave() {
    onSave({
      id: uid(), mode,
      existingId: selId, existingName: selName, existingSku: selSku,
      newName, newKind, newUnit, newPrice, newSku: newSku || skuFrom(newName), skuLocked,
      relType, qtyMode, qtyFixed, qtyVar,
    });
  }

  return (
    <div style={PANEL}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button style={togBtn(mode === 'new')} onClick={() => { setMode('new'); setSelId(''); }}>+ Új cikk létrehozása</button>
        <button style={togBtn(mode === 'existing')} onClick={() => setMode('existing')}>Meglévő keresése</button>
      </div>

      {mode === 'existing' && (
        <div style={{ marginBottom: 10, position: 'relative' }}>
          <label style={LBL}>Cikk *</label>
          {selId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1.5px solid var(--orange)', borderRadius: 7, background: '#fff7ed' }}>
              <code style={{ fontWeight: 700, fontSize: 12 }}>{selSku}</code>
              <span style={{ fontSize: 13 }}>{selName}</span>
              <button style={{ ...DEL, marginLeft: 'auto' }} onClick={() => { setSelId(''); setSelName(''); setSelSku(''); setSearch(''); }}>×</button>
            </div>
          ) : (
            <>
              <input style={INP} value={search} onChange={e => setSearch(e.target.value)}
                onFocus={() => setDropOpen(true)} onBlur={() => setTimeout(() => setDropOpen(false), 200)}
                placeholder="SKU vagy megnevezés..." autoFocus />
              {dropOpen && results.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, border: '1px solid var(--line)', borderRadius: 8, background: '#fff', zIndex: 70, maxHeight: 150, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,.12)', marginTop: 2 }}>
                  {results.map(r => (
                    <button key={r.id} type="button" onMouseDown={() => { setSelId(r.id); setSelName(r.name); setSelSku(r.sku); setSearch(r.name); }}
                      style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid #f3f4f6', background: 'none', cursor: 'pointer', fontSize: 12 }}>
                      <code style={{ fontWeight: 700 }}>{r.sku}</code>
                      <span style={{ color: 'var(--slate)' }}>{r.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {mode === 'new' && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, marginBottom: 8 }}>
            <div><label style={LBL}>Megnevezés *</label><input style={INP} value={newName} onChange={e => setNewName(e.target.value)} placeholder="pl. Hímzés, Szegő anyag" autoFocus /></div>
            <div><label style={LBL}>SKU</label><input style={{ ...INP, fontFamily: 'monospace' }} value={newSku} onChange={e => { setSkuLocked(true); setNewSku(e.target.value); }} /></div>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button style={togBtn(newKind === 'service')} onClick={() => setNewKind('service')}>🔧 Szolgáltatás</button>
            <button style={togBtn(newKind === 'product')} onClick={() => setNewKind('product')}>📦 Anyag / Termék</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
            <div>
              <label style={LBL}>Mértékegység {!newUnitLocked && <span style={{ fontWeight: 400, textTransform: 'none', opacity: .6 }}>(auto)</span>}</label>
              <input list="unit-suggestions" style={INP} value={newUnit}
                onChange={e => { setNewUnitLocked(true); setNewUnit(e.target.value); }}
                placeholder="db, m, h…" />
            </div>
            <div>
              <label style={LBL}>Nettó egységár (Ft) — elhagyható</label>
              <input type="number" inputMode="decimal" style={INP} value={newPrice || ''} onChange={e => setNewPrice(parseFloat(e.target.value) || 0)} placeholder="0" />
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 8 }}>
        <div>
          <label style={LBL}>Kapcsolat típusa</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={togBtn(relType === 'REQUIRED', '#dc2626', '#fef2f2')} onClick={() => setRelType('REQUIRED')}>Kötelező</button>
            <button style={togBtn(relType === 'SUGGESTED', '#15803d', '#f0fdf4')} onClick={() => setRelType('SUGGESTED')}>Javasolt</button>
          </div>
        </div>
        <div>
          <label style={LBL}>Mennyiség</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
            <button style={togBtn(qtyMode === 'fixed')} onClick={() => setQtyMode('fixed')}>Fix szám</button>
            <button style={togBtn(qtyMode === 'var')} onClick={() => setQtyMode('var')}>Képlet változó</button>
          </div>
          {qtyMode === 'fixed' ? (
            <input type="number" inputMode="decimal" style={INP} value={qtyFixed} onChange={e => setQtyFixed(e.target.value)} placeholder="1" />
          ) : (
            <>
              <input style={{ ...INP, fontFamily: 'monospace' }} value={qtyVar} onChange={e => setQtyVar(e.target.value)} placeholder="pl. fm, m2" />
              {formulaVars.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {formulaVars.map(v => <button key={v} type="button" onClick={() => setQtyVar(v)} style={chip('#f0fdf4', '#15803d', '#bbf7d0')}>{v}</button>)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Mégse</Button>
        <Button size="sm" onClick={handleSave} disabled={!ok}>{mode === 'new' ? 'Létrehozás és csatolás' : 'Hozzáadás'}</Button>
      </div>
    </div>
  );
}

// ── RelatedCard ───────────────────────────────────────────────────────────────

function RelatedCard({ r, onDelete }: { r: Related; onDelete: () => void }) {
  const name = r.mode === 'existing' ? r.existingName : r.newName;
  const sku = r.mode === 'existing' ? r.existingSku : r.newSku;
  const ts: React.CSSProperties = r.relType === 'REQUIRED'
    ? { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
    : { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' };
  return (
    <div style={ROW}>
      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, flexShrink: 0, ...ts }}>
        {r.relType === 'REQUIRED' ? 'Kötelező' : 'Javasolt'}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {sku && <code style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)' }}>{sku}</code>}
          <span style={{ fontSize: 13, color: 'var(--slate)' }}>{name || '(új tétel)'}</span>
          {r.mode === 'new' && <span style={{ fontSize: 10, background: '#fff7ed', color: 'var(--orange)', border: '1px solid #fed7aa', borderRadius: 10, padding: '1px 6px', fontWeight: 700 }}>Létrehozandó</span>}
        </div>
        <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 1 }}>
          Mennyiség: {r.qtyMode === 'fixed' ? r.qtyFixed : <code>{r.qtyVar}</code>}
        </div>
      </div>
      <button style={DEL} onClick={onDelete}>×</button>
    </div>
  );
}

// ── JsonPreview ───────────────────────────────────────────────────────────────

function JsonPreview({ name, sku, kind, price, vatRate, specs, formulas, basePriceFormula, validations, related }: {
  name: string; sku: string; kind: string; price: number; vatRate: string;
  specs: Spec[]; formulas: Formula[]; basePriceFormula: string;
  validations: Validation[]; related: Related[];
}) {
  const rules: any[] = [];
  specs.filter(s => s.tipus === 'LISTA').forEach((s, i) => {
    rules.push({ tipus: 'VARIABLE_MAP', sortOrder: i * 10, inputKod: s.kod, mappings: s.opts.map((o, oi) => ({ ertek: o.label, valtozok: { [s.kod]: oi + 1, ...Object.fromEntries(o.extraVars.map(h => [h.key, h.value])) } })) });
    const hasPrices = s.opts.some(o => o.price && parseFloat(o.price) > 0);
    if (hasPrices) rules.push({ tipus: 'VALUE_PRICE', sortOrder: i * 10 + 5, inputKod: s.kod, arak: Object.fromEntries(s.opts.filter(o => parseFloat(o.price) > 0).map(o => [o.label, parseFloat(o.price)])) });
  });
  formulas.forEach((f, i) => rules.push({ tipus: 'SIZE_FORMULA', sortOrder: 100 + i, valtozo: f.resultVar, keplet: f.expression, ...(f.minValue ? { min: f.minValue } : {}) }));
  if (basePriceFormula.trim()) rules.push({ tipus: 'BASE_PRICE_FORMULA', sortOrder: 200, keplet: basePriceFormula });
  validations.forEach((v, i) => rules.push({ tipus: 'CONDITIONAL_ERROR', sortOrder: 300 + i, feltetel: v.conditions.map(c => `${c.left} ${c.op} ${c.right}`).join(` ${v.logic} `), hiba: v.message }));

  const payload = {
    cikk: { sku: sku || skuFrom(name), nev: name, tipus: kind === 'service' ? 'Szolgáltatás' : 'Termék', afa: vatRate + '%', net_ar: price },
    szabalyok: rules,
    kapcsolodok: related.map(r => ({ cikk: r.mode === 'new' ? `(ÚJ) ${r.newName}` : r.existingName, kapcsolat: r.relType, mennyiseg: r.qtyMode === 'fixed' ? r.qtyFixed : `{${r.qtyVar}}` })),
  };
  return (
    <div style={{ background: '#0f172a', borderRadius: 10, padding: 14, overflowY: 'auto', maxHeight: '65vh' }}>
      <pre style={{ fontSize: 11, color: '#e2e8f0', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
        {JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FŐ KOMPONENS
// ─────────────────────────────────────────────────────────────────────────────

interface EditItem {
  id: string; sku: string; name: string; kind: string;
  netUnitPrice: number; vatRate: string; unit?: string;
}

export function ProductBuilderModal({ onClose, onCreated, editItem }: {
  onClose: () => void; onCreated: () => void; editItem?: EditItem;
}) {
  const { toast } = useToast();
  const { activeFeatures, loading: featuresLoading } = useFeatureContext();
  const hasKonfig = featuresLoading || activeFeatures.includes('konfigurator_basic');

  const [viewMode, setViewMode] = useState<'builder' | 'json'>('builder');

  // Alapadatok
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [skuLocked, setSkuLocked] = useState(false);
  const [kind, setKind] = useState<'product' | 'service'>('product');
  const [unit, setUnit] = useState('db');
  const [unitLocked, setUnitLocked] = useState(false);
  const [price, setPrice] = useState('');
  const [vatRate, setVatRate] = useState('27');

  // Specifikációk
  const [specs, setSpecs] = useState<Spec[]>([]);
  const [showAddSpec, setShowAddSpec] = useState(false);

  // Képletek
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [showAddFormula, setShowAddFormula] = useState(false);
  const [basePriceFormula, setBasePriceFormula] = useState('');

  // Feltételek
  const [validations, setValidations] = useState<Validation[]>([]);
  const [showAddVal, setShowAddVal] = useState(false);

  // Kapcsolódó tételek
  const [related, setRelated] = useState<Related[]>([]);
  const [showAddRel, setShowAddRel] = useState(false);

  // Auto-tételek (CONDITIONAL_ITEM szabályok)
  const [autoItemRules, setAutoItemRules] = useState<AutoItemRule[]>([]);
  const [showAddAutoItem, setShowAddAutoItem] = useState(false);

  const [creating, setCreating] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Pre-fill basic fields from editItem
  useEffect(() => {
    if (!editItem) return;
    setName(editItem.name);
    setSku(editItem.sku);
    setSkuLocked(true);
    setKind(editItem.kind as 'product' | 'service');
    if (editItem.unit) { setUnit(editItem.unit); setUnitLocked(true); }
    setPrice(String(editItem.netUnitPrice || ''));
    setVatRate(editItem.vatRate);
  }, [editItem?.id]);

  // Auto-set unit based on kind (unless manually changed)
  useEffect(() => {
    if (!unitLocked) setUnit(kind === 'service' ? 'h' : 'db');
  }, [kind]);

  // Load konfig data for edit mode
  useEffect(() => {
    if (!editItem) return;
    setLoadingEdit(true);
    Promise.all([
      get<any[]>(`/items/${editItem.id}/konfig-inputs`),
      get<any[]>(`/items/${editItem.id}/rules`),
      get<any[]>(`/items/${editItem.id}/related`),
    ]).then(([inputs, rules, relatedItems]) => {
      // Reconstruct specs
      const newSpecs: Spec[] = (inputs || []).map((inp: any) => {
        if (inp.tipus === 'LISTA') {
          const vmRule = rules.find((r: any) => r.ruleType === 'VARIABLE_MAP' && r.ruleParams?.inputKod === inp.kod);
          const vpRule = rules.find((r: any) => r.ruleType === 'VALUE_PRICE' && r.ruleParams?.inputKod === inp.kod);
          const opts: Opt[] = (inp.opciok || []).map((label: string) => {
            const mapping = vmRule?.ruleParams?.mappings?.find((m: any) => m.inputValue === label);
            const optPrice = vpRule?.ruleParams?.values?.[label];
            const extraVars: HVar[] = Object.entries(mapping?.vars || {})
              .filter(([key]) => key !== inp.kod)
              .map(([key, val]) => ({ id: uid(), key, value: String(val) }));
            return { id: uid(), label, price: optPrice != null ? String(optPrice) : '', extraVars };
          });
          return { id: uid(), kod: inp.kod, nev: inp.nev, tipus: 'LISTA' as const, opts, egyseg: inp.egyseg || '', minE: '', maxE: '', sortOrder: inp.sortOrder };
        }
        return {
          id: uid(), kod: inp.kod, nev: inp.nev, tipus: inp.tipus as 'SZAM' | 'SZOVEG',
          opts: [], egyseg: inp.egyseg || '',
          minE: inp.minErtek != null ? String(inp.minErtek) : '',
          maxE: inp.maxErtek != null ? String(inp.maxErtek) : '',
          sortOrder: inp.sortOrder,
        };
      });
      setSpecs(newSpecs);

      // Reconstruct formulas
      const newFormulas: Formula[] = (rules || [])
        .filter((r: any) => r.ruleType === 'SIZE_FORMULA')
        .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
        .map((r: any) => ({
          id: uid(),
          resultVar: r.ruleParams.resultVar,
          expression: r.ruleParams.expression,
          minValue: r.ruleParams.minValue != null ? String(r.ruleParams.minValue) : '',
        }));
      setFormulas(newFormulas);

      // Reconstruct basePriceFormula
      const bpf = (rules || []).find((r: any) => r.ruleType === 'BASE_PRICE_FORMULA');
      setBasePriceFormula(bpf?.ruleParams?.expression || '');

      // Reconstruct validations
      const newValidations: Validation[] = (rules || [])
        .filter((r: any) => r.ruleType === 'CONDITIONAL_ERROR')
        .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
        .map((r: any) => {
          const conds = (r.ruleParams.conditions || []).map((c: any) => ({
            id: uid(), left: c.left, op: c.op, right: String(c.right),
          }));
          const condCount = conds.length;
          const storedLogics: ('AND' | 'OR')[] = r.ruleParams.logics && r.ruleParams.logics.length === condCount - 1
            ? r.ruleParams.logics
            : Array(Math.max(0, condCount - 1)).fill(r.ruleParams.logic || 'AND');
          return { id: uid(), conditions: conds, logics: storedLogics, message: r.ruleParams.message };
        });
      setValidations(newValidations);

      // Reconstruct related
      const newRelated: Related[] = (relatedItems || []).map((rel: any) => ({
        id: uid(), mode: 'existing' as const,
        existingId: rel.relatedItemId, existingName: rel.relatedName || '', existingSku: rel.relatedSku || '',
        newName: '', newKind: 'service' as const, newPrice: 0, newSku: '', skuLocked: false,
        relType: rel.relationType as 'REQUIRED' | 'SUGGESTED',
        qtyMode: rel.qtyFormula ? 'var' as const : 'fixed' as const,
        qtyFixed: rel.qtyFixed != null ? String(rel.qtyFixed) : '1',
        qtyVar: rel.qtyFormula || '',
      }));
      setRelated(newRelated);

      // Reconstruct auto-item rules (CONDITIONAL_ITEM)
      const newAutoItemRules: AutoItemRule[] = [];
      for (const r of (rules || []).filter((r: any) => r.ruleType === 'CONDITIONAL_ITEM')) {
        for (const c of (r.ruleParams.conditions || [])) {
          newAutoItemRules.push({
            id: uid(),
            inputKod: c.inputKod || '',
            inputValue: c.inputValue || '',
            itemSku: c.itemSku || '',
            itemName: c.itemSku || '',
            qtyMode: c.qtyFormula ? 'var' as const : 'fixed' as const,
            qty: String(c.qty ?? 1),
            qtyVar: c.qtyFormula || '',
          });
        }
      }
      setAutoItemRules(newAutoItemRules);
    }).catch(() => {/* silent – toast on save if needed */}).finally(() => setLoadingEdit(false));
  }, [editItem?.id]);

  useEffect(() => { if (!skuLocked) setSku(skuFrom(name)); }, [name]);

  // Elérhető változók a token palette-hoz
  const listaVars = specs.filter(s => s.tipus === 'LISTA').map(s => s.kod);
  const szamVars = specs.filter(s => s.tipus === 'SZAM').map(s => s.kod);
  const extraVarKeys = [...new Set(specs.flatMap(s => s.opts.flatMap(o => o.extraVars.map(h => h.key))))];
  const hasValuePrice = specs.some(s => s.tipus === 'LISTA' && s.opts.some(o => o.price && parseFloat(o.price) > 0));
  const formulaResultVars = formulas.map(f => f.resultVar);
  const allVars = [...listaVars, ...szamVars, ...extraVarKeys, ...(hasValuePrice ? ['egysegar'] : []), ...formulaResultVars];

  // ── Létrehozás ─────────────────────────────────────────────────────────────
  async function handleCreate() {
    if (!name.trim()) { toast('Adj meg egy megnevezést.', 'error'); return; }
    setCreating(true);
    try {
      // 1. Fő cikk létrehozása
      const item = await post<any>('/items', {
        sku: sku.trim() || skuFrom(name),
        name: name.trim(),
        kind,
        unit: unit || (kind === 'service' ? 'h' : 'db'),
        unitNavCode: unitToNavCode(unit || (kind === 'service' ? 'h' : 'db')),
        netUnitPrice: price !== '' ? parseFloat(price) : 0,
        vatRate,
      });
      const itemId: string = item.id;

      if (hasKonfig) {
        // 2. Konfig inputok + VARIABLE_MAP + VALUE_PRICE (spec-enként)
        for (let i = 0; i < specs.length; i++) {
          const s = specs[i];
          await post(`/items/${itemId}/konfig-inputs`, {
            kod: s.kod, nev: s.nev, tipus: s.tipus,
            opciok: s.tipus === 'LISTA' ? s.opts.map(o => o.label) : null,
            egyseg: s.egyseg || null,
            minErtek: s.minE !== '' ? parseFloat(s.minE) : null,
            maxErtek: s.maxE !== '' ? parseFloat(s.maxE) : null,
            sortOrder: i,
          });

          if (s.tipus === 'LISTA') {
            // VARIABLE_MAP: auto-index + egyéni változók minden opcióhoz
            await post(`/items/${itemId}/rules`, {
              ruleType: 'VARIABLE_MAP',
              ruleParams: {
                inputKod: s.kod,
                mappings: s.opts.map((o, oi) => ({
                  inputValue: o.label,
                  vars: {
                    [s.kod]: oi + 1,  // auto-sorszám
                    ...Object.fromEntries(
                      o.extraVars.filter(h => h.key.trim()).map(h => [h.key, parseFloat(h.value) || 0])
                    ),
                  },
                })),
              },
              sortOrder: i * 10,
            });

            // VALUE_PRICE: ha bármely opciónak van ára
            const priced = s.opts.filter(o => o.price && parseFloat(o.price) > 0);
            if (priced.length > 0) {
              await post(`/items/${itemId}/rules`, {
                ruleType: 'VALUE_PRICE',
                ruleParams: {
                  inputKod: s.kod,
                  values: Object.fromEntries(priced.map(o => [o.label, parseFloat(o.price)])),
                  defaultPrice: null,
                },
                sortOrder: i * 10 + 5,
              });
            }
          }
        }

        // 3. SIZE_FORMULA szabályok
        for (let i = 0; i < formulas.length; i++) {
          const f = formulas[i];
          await post(`/items/${itemId}/rules`, {
            ruleType: 'SIZE_FORMULA',
            ruleParams: {
              expression: f.expression,
              resultVar: f.resultVar,
              minValue: f.minValue !== '' ? parseFloat(f.minValue) : null,
            },
            sortOrder: 100 + i,
          });
        }

        // 4. BASE_PRICE_FORMULA
        if (basePriceFormula.trim()) {
          await post(`/items/${itemId}/rules`, {
            ruleType: 'BASE_PRICE_FORMULA',
            ruleParams: { expression: basePriceFormula.trim(), costExpression: null },
            sortOrder: 200,
          });
        }

        // 5. CONDITIONAL_ERROR szabályok
        for (let i = 0; i < validations.length; i++) {
          const v = validations[i];
          await post(`/items/${itemId}/rules`, {
            ruleType: 'CONDITIONAL_ERROR',
            ruleParams: {
              conditions: v.conditions.map(c => ({ left: c.left, op: c.op, right: c.right })),
              logic: v.logics[0] ?? 'AND',  // legacy mező visszafelé kompatibilitáshoz
              logics: v.logics,
              message: v.message,
            },
            sortOrder: 300 + i,
          });
        }

        // 6. CONDITIONAL_ITEM (auto-tételek)
        if (autoItemRules.length > 0) {
          await post(`/items/${itemId}/rules`, {
            ruleType: 'CONDITIONAL_ITEM',
            ruleParams: {
              conditions: autoItemRules.map(r => ({
                inputKod: r.inputKod,
                inputValue: r.inputValue,
                itemSku: r.itemSku,
                qty: parseFloat(r.qty) || 1,
                qtyFormula: r.qtyMode === 'var' && r.qtyVar.trim() ? r.qtyVar.trim() : null,
              })),
            },
            sortOrder: 400,
          });
        }
      }

      // 7. Kapcsolódó tételek
      for (const r of related) {
        let relId = r.existingId;
        if (r.mode === 'new') {
          const niUnit = r.newUnit || (r.newKind === 'service' ? 'h' : 'db');
          const ni = await post<any>('/items', {
            sku: r.newSku || skuFrom(r.newName),
            name: r.newName, kind: r.newKind,
            unit: niUnit,
            unitNavCode: unitToNavCode(niUnit),
            netUnitPrice: r.newPrice, vatRate: '27',
          });
          relId = ni.id;
        }
        await post(`/items/${itemId}/related`, {
          relatedItemId: relId,
          relationType: r.relType,
          variantMatched: false,
          qtyFixed: r.qtyMode === 'fixed' && r.qtyFixed !== '' ? parseFloat(r.qtyFixed) : null,
          qtyFormula: r.qtyMode === 'var' && r.qtyVar.trim() ? r.qtyVar.trim() : null,
        });
      }

      toast(`"${name}" sikeresen létrehozva!`, 'success');
      onCreated();
      onClose();
    } catch (e: any) {
      toast(e.message || 'Létrehozás sikertelen.', 'error');
    }
    setCreating(false);
  }

  // ── Szerkesztés mentése ────────────────────────────────────────────────────
  async function handleSave() {
    if (!editItem) return;
    if (!name.trim()) { toast('Adj meg egy megnevezést.', 'error'); return; }
    setCreating(true);
    try {
      const itemId = editItem.id;

      // 1. Alapadatok frissítése
      await patch(`/items/${itemId}`, {
        name: name.trim(),
        netUnitPrice: price !== '' ? parseFloat(price) : 0,
        vatRate, kind,
        unit: unit || (kind === 'service' ? 'h' : 'db'),
        unitNavCode: unitToNavCode(unit || (kind === 'service' ? 'h' : 'db')),
      });

      if (hasKonfig) {
        // 2. Meglévő konfig törlése
        const [existingInputs, existingRules, existingRelated] = await Promise.all([
          get<any[]>(`/items/${itemId}/konfig-inputs`),
          get<any[]>(`/items/${itemId}/rules`),
          get<any[]>(`/items/${itemId}/related`),
        ]);
        for (const inp of (existingInputs || [])) await del(`/items/${itemId}/konfig-inputs/${inp.id}`);
        for (const rule of (existingRules || [])) await del(`/items/${itemId}/rules/${rule.id}`);
        for (const rel of (existingRelated || [])) await del(`/items/${itemId}/related/${rel.id}`);

        // 3. Konfig inputok + VARIABLE_MAP + VALUE_PRICE újraépítése
        for (let i = 0; i < specs.length; i++) {
          const s = specs[i];
          await post(`/items/${itemId}/konfig-inputs`, {
            kod: s.kod, nev: s.nev, tipus: s.tipus,
            opciok: s.tipus === 'LISTA' ? s.opts.map(o => o.label) : null,
            egyseg: s.egyseg || null,
            minErtek: s.minE !== '' ? parseFloat(s.minE) : null,
            maxErtek: s.maxE !== '' ? parseFloat(s.maxE) : null,
            sortOrder: i,
          });
          if (s.tipus === 'LISTA') {
            await post(`/items/${itemId}/rules`, {
              ruleType: 'VARIABLE_MAP',
              ruleParams: {
                inputKod: s.kod,
                mappings: s.opts.map((o, oi) => ({
                  inputValue: o.label,
                  vars: { [s.kod]: oi + 1, ...Object.fromEntries(o.extraVars.filter(h => h.key.trim()).map(h => [h.key, parseFloat(h.value) || 0])) },
                })),
              },
              sortOrder: i * 10,
            });
            const priced = s.opts.filter(o => o.price && parseFloat(o.price) > 0);
            if (priced.length > 0) {
              await post(`/items/${itemId}/rules`, {
                ruleType: 'VALUE_PRICE',
                ruleParams: { inputKod: s.kod, values: Object.fromEntries(priced.map(o => [o.label, parseFloat(o.price)])), defaultPrice: null },
                sortOrder: i * 10 + 5,
              });
            }
          }
        }

        // 4. SIZE_FORMULA
        for (let i = 0; i < formulas.length; i++) {
          const f = formulas[i];
          await post(`/items/${itemId}/rules`, {
            ruleType: 'SIZE_FORMULA',
            ruleParams: { expression: f.expression, resultVar: f.resultVar, minValue: f.minValue !== '' ? parseFloat(f.minValue) : null },
            sortOrder: 100 + i,
          });
        }

        // 5. BASE_PRICE_FORMULA
        if (basePriceFormula.trim()) {
          await post(`/items/${itemId}/rules`, {
            ruleType: 'BASE_PRICE_FORMULA',
            ruleParams: { expression: basePriceFormula.trim(), costExpression: null },
            sortOrder: 200,
          });
        }

        // 6. CONDITIONAL_ERROR
        for (let i = 0; i < validations.length; i++) {
          const v = validations[i];
          await post(`/items/${itemId}/rules`, {
            ruleType: 'CONDITIONAL_ERROR',
            ruleParams: {
              conditions: v.conditions.map(c => ({ left: c.left, op: c.op, right: c.right })),
              logic: v.logics[0] ?? 'AND',
              logics: v.logics,
              message: v.message,
            },
            sortOrder: 300 + i,
          });
        }

        // 7. CONDITIONAL_ITEM (auto-tételek)
        if (autoItemRules.length > 0) {
          await post(`/items/${itemId}/rules`, {
            ruleType: 'CONDITIONAL_ITEM',
            ruleParams: {
              conditions: autoItemRules.map(r => ({
                inputKod: r.inputKod,
                inputValue: r.inputValue,
                itemSku: r.itemSku,
                qty: parseFloat(r.qty) || 1,
                qtyFormula: r.qtyMode === 'var' && r.qtyVar.trim() ? r.qtyVar.trim() : null,
              })),
            },
            sortOrder: 400,
          });
        }
      }

      // 8. Kapcsolódó tételek újraépítése
      for (const r of related) {
        let relId = r.existingId;
        if (r.mode === 'new') {
          const niUnit = r.newUnit || (r.newKind === 'service' ? 'h' : 'db');
          const ni = await post<any>('/items', {
            sku: r.newSku || skuFrom(r.newName),
            name: r.newName, kind: r.newKind,
            unit: niUnit,
            unitNavCode: unitToNavCode(niUnit),
            netUnitPrice: r.newPrice, vatRate: '27',
          });
          relId = ni.id;
        }
        await post(`/items/${itemId}/related`, {
          relatedItemId: relId, relationType: r.relType, variantMatched: false,
          qtyFixed: r.qtyMode === 'fixed' && r.qtyFixed !== '' ? parseFloat(r.qtyFixed) : null,
          qtyFormula: r.qtyMode === 'var' && r.qtyVar.trim() ? r.qtyVar.trim() : null,
        });
      }

      toast(`"${name}" sikeresen mentve!`, 'success');
      onCreated();
      onClose();
    } catch (e: any) {
      toast(e.message || 'Mentés sikertelen.', 'error');
    }
    setCreating(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="overlay show" style={{ display: 'flex', padding: 16, alignItems: 'flex-start', paddingTop: 28 }} onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600, width: '100%', margin: '0 auto', maxHeight: '94vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-h" style={{ flexShrink: 0 }}>
          <h3 style={{ fontSize: 15 }}>{editItem ? 'Cikk szerkesztése' : 'Termék Építő'}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginRight: 8 }}>
            <span style={{ fontSize: 11, color: viewMode === 'builder' ? 'var(--orange)' : 'var(--slate)' }}>Építő</span>
            <button onClick={() => setViewMode(m => m === 'builder' ? 'json' : 'builder')}
              style={{ width: 42, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', position: 'relative', background: viewMode === 'json' ? '#1d4ed8' : '#d1d5db', transition: 'background .2s', padding: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: viewMode === 'json' ? 22 : 2, width: 18, height: 18, borderRadius: 9, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .2s' }} />
            </button>
            <span style={{ fontSize: 11, color: viewMode === 'json' ? '#1d4ed8' : 'var(--slate)' }}>JSON</span>
          </div>
          <button onClick={onClose} className="x">&times;</button>
        </div>

        {/* JSON nézet */}
        {viewMode === 'json' && (
          <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
            <div style={{ fontSize: 12, color: 'var(--slate)', marginBottom: 8 }}>Pontosan ez kerül a rendszerbe a Létrehozás gombkor:</div>
            <JsonPreview name={name} sku={sku} kind={kind} price={parseFloat(price) || 0} vatRate={vatRate}
              specs={specs} formulas={formulas} basePriceFormula={basePriceFormula}
              validations={validations} related={related} />
          </div>
        )}

        {/* Építő nézet */}
        {viewMode === 'builder' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>

            {/* ── Alapadatok ── */}
            <div style={{ paddingTop: 14, marginBottom: 4 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 10 }}>Alapadatok</div>
              <div style={{ marginBottom: 10 }}>
                <label style={LBL}>Megnevezés *</label>
                <input style={{ ...INP, fontSize: 15, fontWeight: 600 }} value={name} onChange={e => setName(e.target.value)}
                  placeholder="pl. Egyedileg hímzett lábtörlő" autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={LBL}>SKU {!skuLocked && <span style={{ fontWeight: 400, textTransform: 'none', opacity: .6 }}>(auto)</span>}</label>
                  <input style={{ ...INP, fontFamily: 'monospace' }} value={sku} onChange={e => { setSkuLocked(true); setSku(e.target.value); }} />
                </div>
                <div>
                  <label style={LBL}>Mértékegység {!unitLocked && <span style={{ fontWeight: 400, textTransform: 'none', opacity: .6 }}>(auto)</span>}</label>
                  <input list="unit-suggestions" style={INP} value={unit}
                    onChange={e => { setUnitLocked(true); setUnit(e.target.value); }}
                    placeholder="db, m, m2, kg, h…" />
                  <datalist id="unit-suggestions">
                    <option value="db" /><option value="m" /><option value="m2" />
                    <option value="m3" /><option value="kg" /><option value="h" />
                    <option value="nap" /><option value="l" /><option value="cs" />
                  </datalist>
                </div>
                <div>
                  <label style={LBL}>Nettó egységár (Ft)</label>
                  <input type="number" inputMode="decimal" style={INP} value={price} onChange={e => setPrice(e.target.value)} placeholder="0 (ha képlettel)" />
                </div>
                <div>
                  <label style={LBL}>ÁFA</label>
                  <select value={vatRate} onChange={e => setVatRate(e.target.value)} className="form-input" style={{ fontSize: 13 }}>
                    <option value="27">27%</option><option value="18">18%</option>
                    <option value="5">5%</option><option value="0">0%</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={{ ...togBtn(kind === 'product'), flex: 1, padding: '9px' }} onClick={() => setKind('product')}>📦 Termék</button>
                <button style={{ ...togBtn(kind === 'service'), flex: 1, padding: '9px' }} onClick={() => setKind('service')}>🔧 Szolgáltatás</button>
              </div>
            </div>

            {/* ── Specifikációk ── */}
            <Section title="Specifikációk"
              subtitle="LISTA: választó opciókkal (auto-sorszám + opcionális ár + haladó egyéni változók) · SZÁM: méret/érték · SZÖVEG: szabad"
              locked={!hasKonfig}
              onAdd={!showAddSpec ? () => setShowAddSpec(true) : undefined} addLabel="+ Spec">
              {showAddSpec && (
                <SpecFormPanel sortOrder={specs.length}
                  onSave={s => { setSpecs(p => [...p, s]); setShowAddSpec(false); }}
                  onCancel={() => setShowAddSpec(false)} />
              )}
              {specs.map(s => <SpecCard key={s.id} s={s} onDelete={() => setSpecs(p => p.filter(x => x.id !== s.id))} />)}
              {specs.length === 0 && !showAddSpec && (
                <div style={{ fontSize: 11, color: 'var(--slate)', padding: '4px 0', opacity: .7 }}>
                  Pl. "Szín" (Lista: Fehér 30.000 Ft, Antracit 33.000 Ft) · "Méret" (Lista: 35×100 → szel=35,mag=100) · "Szélesség" (Szám, cm)
                </div>
              )}
            </Section>

            {/* ── Képletek ── */}
            <Section title="Képletek (méret / köztes értékek)"
              subtitle="SIZE_FORMULA: köztes változók számítása (pl. fm = 2*szel + 2*mag)"
              locked={!hasKonfig}
              onAdd={!showAddFormula ? () => setShowAddFormula(true) : undefined} addLabel="+ Képlet">
              {showAddFormula && (
                <FormulaFormPanel availVars={allVars.filter(v => v !== basePriceFormula)}
                  onSave={f => { setFormulas(p => [...p, f]); setShowAddFormula(false); }}
                  onCancel={() => setShowAddFormula(false)} />
              )}
              {formulas.map(f => <FormulaCard key={f.id} f={f} onDelete={() => setFormulas(p => p.filter(x => x.id !== f.id))} />)}

              {/* Végső ár képlete */}
              <div style={{ marginTop: 10, padding: 12, background: '#f8faff', border: '1px solid #e0e7ff', borderRadius: 8 }}>
                <label style={{ ...LBL, color: '#4338ca' }}>Végső ár képlete (BASE_PRICE_FORMULA) — opcionális</label>
                <div style={{ fontSize: 11, color: 'var(--slate)', marginBottom: 6 }}>
                  Ha az ár számított (pl. <code>egysegar * m2</code>), itt add meg.
                  {hasValuePrice && <> Az <code>egysegar</code> változó az opció áránál megadott értéket veszi fel.</>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input style={{ ...INP, fontFamily: 'monospace', flex: 1 }} value={basePriceFormula}
                    onChange={e => setBasePriceFormula(e.target.value)} placeholder="pl. egysegar * m2" />
                  {basePriceFormula && <button style={DEL} onClick={() => setBasePriceFormula('')}>×</button>}
                </div>
                {allVars.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {allVars.map(v => (
                      <button key={v} onClick={() => setBasePriceFormula(f => f + (f && !f.endsWith(' ') ? ' ' : '') + v + ' ')}
                        style={chip('#eff6ff', '#1d4ed8', '#bfdbfe')}>{v}</button>
                    ))}
                    {['+', '-', '*', '/'].map(op => (
                      <button key={op} onClick={() => setBasePriceFormula(f => f + ' ' + op + ' ')}
                        style={{ ...chip('#f3f4f6', 'var(--navy)', '#e5e7eb'), fontFamily: 'monospace', fontWeight: 700 }}>{op}</button>
                    ))}
                  </div>
                )}
              </div>

              {formulas.length === 0 && !showAddFormula && (
                <div style={{ fontSize: 11, color: 'var(--slate)', padding: '4px 0', opacity: .7 }}>
                  Elhagyható · ha pl. a szegő folyóméterét kell kiszámítani a méretből
                </div>
              )}
            </Section>

            {/* ── Hibafeltételek ── */}
            <Section title="Hibafeltételek"
              subtitle="Ha a feltétel teljesül → hibaüzenet (pl. tiltott szín+méret kombináció)"
              locked={!hasKonfig}
              onAdd={specs.length > 0 && !showAddVal ? () => setShowAddVal(true) : undefined} addLabel="+ Feltétel">
              {specs.length === 0 && !showAddVal && (
                <div style={{ fontSize: 11, color: 'var(--slate)', padding: '4px 0', opacity: .7 }}>Előbb adj hozzá specifikációkat.</div>
              )}
              {showAddVal && (
                <ValidationFormPanel specs={specs} formulaVars={formulaResultVars}
                  onSave={v => { setValidations(p => [...p, v]); setShowAddVal(false); }}
                  onCancel={() => setShowAddVal(false)} />
              )}
              {validations.map(v => <ValidationCard key={v.id} v={v} onDelete={() => setValidations(p => p.filter(x => x.id !== v.id))} />)}
            </Section>

            {/* ── Auto-tételek (feltételalapú) ── */}
            <Section title="Auto-tételek (feltétel alapján)"
              subtitle="Ha konfig mező = adott érték → automatikusan hozzáadja a megrendeléshez a megadott terméket"
              locked={!hasKonfig}
              onAdd={specs.length > 0 && !showAddAutoItem ? () => setShowAddAutoItem(true) : undefined} addLabel="+ Szabály">
              {specs.length === 0 && !showAddAutoItem && (
                <div style={{ fontSize: 11, color: 'var(--slate)', padding: '4px 0', opacity: .7 }}>Előbb adj hozzá specifikációkat.</div>
              )}
              {showAddAutoItem && (
                <AutoItemRuleEditor specs={specs} formulaVars={formulaResultVars}
                  onSave={r => { setAutoItemRules(p => [...p, r]); setShowAddAutoItem(false); }}
                  onCancel={() => setShowAddAutoItem(false)} />
              )}
              {autoItemRules.map(r => (
                <div key={r.id} style={ROW}>
                  <div style={{ flex: 1, fontSize: 12 }}>
                    <span>HA </span>
                    <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 4 }}>{r.inputKod} = {r.inputValue}</code>
                    <span> → hozzáad: </span>
                    <code style={r.itemSku.includes('{') ? { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '1px 6px', borderRadius: 4 } : { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', padding: '1px 6px', borderRadius: 4 }}>{r.itemSku}</code>
                    {r.itemName && r.itemName !== r.itemSku && <span style={{ color: 'var(--slate)', fontSize: 11 }}> {r.itemName}</span>}
                    <span style={{ color: 'var(--slate)' }}>
                      {r.qtyMode === 'var' && r.qtyVar ? ` × ${r.qtyVar} (képlet)` : ` × ${r.qty} db`}
                    </span>
                  </div>
                  <button style={DEL} onClick={() => setAutoItemRules(p => p.filter(x => x.id !== r.id))}>×</button>
                </div>
              ))}
              {autoItemRules.length === 0 && !showAddAutoItem && specs.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--slate)', padding: '4px 0', opacity: .7 }}>
                  Pl. ha kezelés = motoros → add hozzá: MOTOR-R13 × 1 db
                </div>
              )}
            </Section>

            {/* ── Kapcsolódó tételek ── */}
            <Section title="Kapcsolódó tételek"
              subtitle="Kötelező/javasolt anyagok és szolgáltatások · mennyiség fix vagy képlet-változó (pl. fm)"
              onAdd={!showAddRel ? () => setShowAddRel(true) : undefined} addLabel="+ Tétel">
              {showAddRel && (
                <AddRelatedPanel formulaVars={formulaResultVars}
                  onSave={r => { setRelated(p => [...p, r]); setShowAddRel(false); }}
                  onCancel={() => setShowAddRel(false)} />
              )}
              {related.map(r => <RelatedCard key={r.id} r={r} onDelete={() => setRelated(p => p.filter(x => x.id !== r.id))} />)}
              {related.length === 0 && !showAddRel && (
                <div style={{ fontSize: 11, color: 'var(--slate)', padding: '4px 0', opacity: .7 }}>
                  Pl. "Hímzés" (Szolgáltatás, qty=1) · "Szegő anyag" (Termék, qty=fm képlet-változó)
                </div>
              )}
            </Section>

          </div>
        )}

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: '12px 16px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
          <span style={{ fontSize: 11, color: 'var(--slate)' }}>
            {loadingEdit ? 'Betöltés…' : `${specs.length} spec · ${formulas.length + (basePriceFormula ? 1 : 0)} képlet · ${validations.length} feltétel · ${autoItemRules.length} auto · ${related.length} kapcsolódó`}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Mégse</Button>
            {editItem
              ? <Button onClick={handleSave} loading={creating} disabled={!name.trim() || loadingEdit}>Mentés</Button>
              : <Button onClick={handleCreate} loading={creating} disabled={!name.trim()}>Létrehozás</Button>
            }
          </div>
        </div>
      </div>
    </div>
  );
}
